import React, { useMemo, useEffect } from 'react';
import { LineChart } from '@/components/ui/chart-wrapper';
import DataTable from './DataTable';
import WidgetBase from './WidgetBase';
import { useWidgetData } from '../../hooks/useWidgetData';
import { normalizeCell } from '../../utils/exportToCSV';
import { useChartColors } from '../../hooks/useChartColors';
// exportChartDataToCSV is intentionally not used here — lineDataSet uses {x,y} objects
// which have no flat labels array, causing that helper to bail out silently.

/**
 * Line Widget Component
 * Modernized replacement for LineList.js
 * Displays time-series data as line chart or table
 */
function LineWidget({ data, onLegendUpdate, onExportReady, onDataReady, mode = 'chart' }) {
    const colors = useChartColors();

    // Fetch data using custom hook
    const { data: apiData, loading, error } = useWidgetData(
        data.url,
        data.RequestJson,
        data.RequestJson1
    );

    // Transform API data to Chart.js format
    // API response field mapping (from old LineList.js):
    //   - lineDataSet[0].datasets → array of {x, y} objects (Chart.js scatter/line format)
    //   - lineDataSet[0].toplabel → series label
    //   - listDataSet             → table rows
    const chartData = useMemo(() => {
        if (!apiData || apiData.length === 0) return null;

        try {
            const lineSet = apiData[0].lineDataSet?.[0];
            if (!lineSet || !lineSet.datasets || lineSet.datasets.length === 0) return null;

            // Data is already in {x, y} format - Chart.js line charts support this directly
            // The datasets array itself is the data (each element is {x: dateString, y: number})
            return {
                datasets: [{
                    label: lineSet.toplabel || data.YlabelString || 'Value',
                    data: lineSet.datasets,  // [{x: '2024-01-01', y: 100}, ...]
                    borderColor: colors.chart1,
                    backgroundColor: colors.background,
                    pointBackgroundColor: colors.background,
                    pointBorderColor: colors.chart1,
                    pointBorderWidth: 2,
                    tension: 0.4,
                    fill: false,
                    hoverBackgroundColor: colors.chart2
                }]
            };
        } catch (err) {
            console.error('Error transforming line chart data:', err);
            return null;
        }
    }, [apiData, data, colors]);

    // Transform listDataSet to table format (same structure as BarWidget/TableWidget)
    const { tableData, columns } = useMemo(() => {
        if (!apiData || apiData.length === 0) return { tableData: [], columns: [] };

        try {
            const listDataSet = apiData[0].listDataSet;
            if (!listDataSet || listDataSet.length === 0) return { tableData: [], columns: [] };

            // First row defines column headers (header text is in .data field)
            const cols = (listDataSet[0].datasets || []).map((ds, idx) => ({
                key: `col${idx}`,
                label: ds.data || `Column ${idx + 1}`,
                sortable: true,
                format: (val) => val ?? '-'
            }));

            // Data rows start at index 1 (index 0 is the header row)
            const rows = listDataSet.slice(1).map(item => {
                const row = {};
                (item.datasets || []).forEach((ds, idx) => {
                    row[`col${idx}`] = ds.data;
                });
                return row;
            });

            return { tableData: rows, columns: cols };
        } catch (err) {
            console.error('Error transforming line table data:', err);
            return { tableData: [], columns: [] };
        }
    }, [apiData]);

    // Notify parent whether this widget has data (used to disable toggle / export buttons).
    // Use derived chartData/tableData — raw apiData can be non-empty with no real content.
    useEffect(() => {
        if (loading) return;
        if (onDataReady) onDataReady(chartData !== null || tableData.length > 0);
    }, [loading, chartData, tableData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Export handler — mirrors Bridge's LineList.js ExportWidget which iterates listDataSet directly.
    // listDataSet[0] is the header row; subsequent rows are data strings.
    // chartData uses {x,y} objects so we cannot use exportChartDataToCSV here.
    const handleExport = () => {
        if (!apiData || apiData.length === 0) return;
        try {
            const listDataSet = apiData[0].listDataSet;
            if (!listDataSet || listDataSet.length === 0) return;

            const tableRows = listDataSet.map((row, rowIdx) =>
                (row.datasets || []).map((ds, colIdx) => {
                    // Row 0 is the header row — never alter headers.
                    // Column 0 on data rows is always the date column: unconditionally prefix
                    // with a space so all spreadsheet apps treat it as plain text.
                    // (Matches Bridge_API_Reference_Procedure BarList.js ExportWidget behaviour.)
                    if (rowIdx > 0 && colIdx === 0) {
                        return ' ' + String(ds.data ?? '').replace(/,/g, '');
                    }
                    return normalizeCell(ds.data);
                }).join(',')
            );

            const csvContent = tableRows.join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `${data.title || 'line-chart-data'}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error exporting LineWidget data:', err);
        }
    };

    // Report legend value to parent (number shown before title in card header)
    useEffect(() => {
        if (!apiData || apiData.length === 0 || !onLegendUpdate) return;
        try {
            const legendVal = apiData[0].legend?.[0]?.legendValue ?? '';
            if (legendVal) onLegendUpdate(data.id, String(legendVal));
        } catch (e) { /* ignore */ }
    }, [apiData]);

    // Register export handler with parent (for header-level export button)
    useEffect(() => {
        if (onExportReady) onExportReady(handleExport);
    }, [apiData]);

    // Chart options — x-axis uses 'category' since data is {x: label, y: value}
    const chartOptions = {
        scales: {
            x: {
                type: 'category',
                title: {
                    // When hideXTicks is set, suppress the bottom axis title text too
                    display: !data.hideXTicks && !!(data.XlabelString),
                    text: data.XlabelString || ''
                },
                ticks: {
                    display: !data.hideXTicks,
                    maxTicksLimit: 12,
                    maxRotation: 45,
                    minRotation: 0
                },
                grid: {
                    // Remove vertical grid lines when hideXTicks is set
                    display: !data.hideXTicks
                }
            },
            y: {
                title: {
                    // Hide Y-axis label text as well when hideXTicks is set
                    display: !data.hideXTicks && !!(data.YlabelString),
                    text: data.YlabelString || ''
                },
                beginAtZero: true
            }
        },
        plugins: {
            legend: {
                display: false
            }
        }
    };

    return (
        <WidgetBase
            loading={loading}
            error={error}
            chartComponent={
                chartData ? (
                    <LineChart
                        data={chartData}
                        options={chartOptions}
                        height={280}
                    />
                ) : (
                    <div className="flex items-center justify-center py-12">
                        <p className="text-primary/70">No Data Available</p>
                    </div>
                )
            }
            tableComponent={
                tableData.length > 0 ? (
                    <DataTable
                        columns={columns}
                        data={tableData}
                        pagination={{ enabled: true, itemsPerPage: 6 }}
                        sortable={true}
                        searchable={false}
                    />
                ) : (
                    <div className="flex items-center justify-center py-12">
                        <p className="text-primary/70">No Data Available</p>
                    </div>
                )
            }
            mode={mode}
        />
    );
}

export default LineWidget;
